/* ===================================================================
 * Cortex Motion — orchestration des entrées au scroll
 * - .cx-reveal      → fade-up à l'entrée dans le viewport (une fois)
 * - .cx-stagger     → cascade les enfants (--cx-i = index)
 * - .cx-counter     → incrémente un nombre lu dans data-target
 * - .cx-underline-draw → souligné teal qui se trace
 * Auto-init : tout élément déjà présent au DOMContentLoaded.
 * Re-scan : window.CortexMotion.scan() après injection dynamique.
 * Respect : prefers-reduced-motion → no-op (CSS gère l'état final).
 * =================================================================== */
(function () {
  if (window.__cortexMotionInit) return;
  window.__cortexMotionInit = true;

  var prefersReduced =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Si reduced-motion, on marque tout en is-in immédiatement et on sort.
  if (prefersReduced) {
    document.addEventListener('DOMContentLoaded', function () {
      document
        .querySelectorAll('.cx-reveal, .cx-stagger, .cx-underline-draw, [data-cx-stagger]')
        .forEach(function (el) { el.classList.add('is-in'); });
    });
    window.CortexMotion = { scan: function () {} };
    return;
  }

  // ---------- Stagger : assigner --cx-i sur les enfants ----------
  function applyStaggerIndex(root) {
    var groups = root.querySelectorAll('.cx-stagger, [data-cx-stagger]');
    groups.forEach(function (group) {
      var children = group.children;
      for (var i = 0; i < children.length; i++) {
        children[i].style.setProperty('--cx-i', i);
      }
    });
  }

  // ---------- IntersectionObserver — entrée déclenchée une seule fois ----------
  var io = null;
  function getObserver() {
    if (io) return io;
    io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          el.classList.add('is-in');

          // Si c'est un counter, lance l'incrément
          if (el.classList.contains('cx-counter')) {
            animateCounter(el, 0);
          }
          // Si c'est un parent qui contient des counters, les démarrer avec le même
          // décalage que la cascade stagger (lecture de --cx-i sur le child porteur).
          var counters = el.querySelectorAll && el.querySelectorAll('.cx-counter:not(.is-counted)');
          if (counters && counters.length) {
            counters.forEach(function (c) {
              var child = c.closest(el.tagName.toLowerCase() + ' > *') ||
                          (function () {
                            var cur = c;
                            while (cur && cur.parentElement !== el) cur = cur.parentElement;
                            return cur;
                          })();
              var idx = child ? parseInt(child.style.getPropertyValue('--cx-i') || '0', 10) : 0;
              animateCounter(c, idx * 70 + 80);
            });
          }

          io.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    return io;
  }

  // ---------- Counter — anime le nombre cible avec délai initial ----------
  function animateCounter(el, delayMs) {
    if (el.classList.contains('is-counted')) return;
    el.classList.add('is-counted');

    var raw = el.getAttribute('data-target') || el.textContent.trim();
    // Match un nombre qui peut contenir des espaces de millier ou des virgules,
    // mais qui DOIT commencer ET finir par un digit (évite de capturer un espace trailing).
    var match = raw.match(/(\d(?:[\d.,\s]*\d)?)/);
    if (!match) return;
    var endNum = parseFloat(match[1].replace(/[\s,]/g, '')) || 0;
    if (!endNum) return;

    var prefix = raw.slice(0, match.index);
    var suffix = raw.slice(match.index + match[1].length);
    var duration = 1100;
    var ease = function (t) { return 1 - Math.pow(1 - t, 4); }; // ease-out-quart

    // État initial : "0" (préserve le contexte)
    var initial = endNum >= 1000 ? '0' : '0';
    el.textContent = prefix + initial + suffix;

    function run() {
      var start = null;
      function step(ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / duration, 1);
        var v = endNum * ease(p);
        var formatted = v >= 1000
          ? Math.round(v).toLocaleString('fr-FR').replace(/,/g, ' ')
          : v < 10 && (endNum % 1 !== 0)
              ? v.toFixed(1)
              : Math.round(v).toString();
        el.textContent = prefix + formatted + suffix;
        if (p < 1) requestAnimationFrame(step);
        else el.textContent = raw; // état final exact (préserve format original)
      }
      requestAnimationFrame(step);
    }

    if (delayMs && delayMs > 0) setTimeout(run, delayMs);
    else run();
  }

  // ---------- Scan + observe ----------
  function scan(root) {
    root = root || document;
    applyStaggerIndex(root);

    var observer = getObserver();
    var targets = root.querySelectorAll(
      '.cx-reveal:not(.is-in), .cx-stagger:not(.is-in), .cx-underline-draw:not(.is-in), [data-cx-stagger]:not(.is-in), .cx-counter:not(.is-counted)'
    );
    targets.forEach(function (el) {
      // Si le counter est à l'intérieur d'un .cx-stagger / [data-cx-stagger],
      // on laisse le parent piloter son animation (synchro avec le fade-up cascadé).
      if (
        el.classList.contains('cx-counter') &&
        el.closest('.cx-stagger, [data-cx-stagger]')
      ) {
        return;
      }
      observer.observe(el);
    });
  }

  // ---------- Auto-tag : marque automatiquement les targets standards ----------
  // Permet d'animer les sections existantes SANS toucher les Liquid (zero-touch).
  function autoTag() {
    var rules = [
      // Eyebrow + heading — chaque section
      ['.cortex-section .cortex-eyebrow', 'cx-reveal'],
      ['.cortex-section h2:not(.cortex-rassurance__heading)', 'cx-reveal'],
      // Constat / Guerre — stat numbers (counters) + grid (stagger)
      ['.cortex-stat-grid', 'cx-stagger'],
      ['.cortex-stat__num', 'cx-counter'],
      // Pourquoi raté — table rows en stagger via wrapper
      ['.cortex-fail-table tbody', 'cx-stagger'],
      // Solution — grid 3-cards
      ['.cortex-grid-3', 'cx-stagger'],
      // Pullquotes
      ['.cortex-pullquote', 'cx-reveal'],
      // Bypass phones
      ['.cortex-bypass__phone', 'cx-reveal'],
      // Rassurance card
      ['.cortex-rassurance__card', 'cx-reveal'],
      // Tip block (solution)
      ['.cortex-tip', 'cx-reveal'],
      // Legal box (loi)
      ['.cortex-legal-box', 'cx-reveal'],
      // FAQ items in stagger
      ['.cortex-faq', 'cx-stagger'],
      // CTA final wrapper
      ['.cortex-cta-final .cortex-wrap', 'cx-reveal'],
      // Comment ça marche (tutoriel)
      ['.cccm__head', 'cx-reveal'],
      ['.cccm__carousel', 'cx-stagger'],
      ['.cccm__cta-wrap', 'cx-reveal'],
      // Garantie — pillars en stagger
      ['.cortex-pillars', 'cx-stagger'],
      // Anchor / pourquoi-ca-marche listes
      ['.cortex-list', 'cx-stagger'],
      // Pourquoi-ca-marche grid alternatif
      ['.cortex-pcm__grid', 'cx-stagger'],
      // UGC témoignages
      ['.cortex-ugc-track', 'cx-stagger'],
    ];
    rules.forEach(function (r) {
      document.querySelectorAll(r[0]).forEach(function (el) {
        if (!el.classList.contains(r[1])) el.classList.add(r[1]);
      });
    });
  }

  function init() {
    autoTag();
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan API (utilisable après injection dynamique)
  window.CortexMotion = { scan: scan, autoTag: autoTag };
})();
